import React, { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Pencil } from "lucide-react";

interface AddStationFlowProps {
  onBack: () => void;
}

const steps = [
  "Basic information",
  "Location details",
  "Charger configuration",
  "Pricing & operations",
  "Review & submit",
];

const AddStationFlow: React.FC<AddStationFlowProps> = ({ onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    stationName: "",
    stationId: "",
    stationType: "Public",
    pocName: "",
    pocNumber: "",
    pocEmail: "",
    chargerType: "AC charger",
    connectorType: "",
    chargerCapacity: "",
    connectivity: "",
    stationAccessType: "",
    chargerAccessType: "",
    streetAddress: "",
    state: "",
    pincode: "",
    latitude: "",
    longitude: "",
    availableParkingSpace: "",
    description: "",
    tariffType: "",
    priceModel: "",
    currency: "INR",
    pricePerKwh: "",
    isRoundTheClock: false,
  });

  const progressPercent = useMemo(
    () => Math.round((currentStep / steps.length) * 100),
    [currentStep]
  );

  const handleChange =
    (field: keyof typeof formData) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleToggleRoundTheClock = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, isRoundTheClock: event.target.checked }));
  };

  const goToNextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const showValue = (value: string | boolean) => {
    if (typeof value === "boolean") {
      return value ? "Enabled" : "Disabled";
    }
    return value?.trim() ? value : "-";
  };

  return (
    <div className="add-station-flow">
      <div className="add-station-topbar">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          Add new charging station
        </button>
      </div>

      <div className="add-station-content">
        <section className="add-station-card progress-card">
          <div className="progress-header">
            <h2>Progress</h2>
            <span>{progressPercent}% complete</span>
          </div>
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div className="steps-list">
            {steps.map((label, index) => {
              const stepNumber = index + 1;
              const isActive = stepNumber === currentStep;
              const isCompleted = stepNumber < currentStep;

              return (
                <div key={label} className="step-item">
                  <div
                    className={`step-dot ${isActive ? "active" : ""} ${
                      isCompleted ? "completed" : ""
                    }`}
                  >
                    {stepNumber}
                  </div>
                  <p>{label}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="add-station-card form-card">
          {currentStep === 1 ? (
            <>
              <h3>Basic information</h3>

              <div className="station-form-grid single-column">
                <label>
                  Station name *
                  <input
                    type="text"
                    placeholder="123 street name, city, extra info"
                    value={formData.stationName}
                    onChange={handleChange("stationName")}
                  />
                </label>
              </div>

              <div className="station-form-grid two-column">
                <label>
                  Station ID *
                  <input
                    type="text"
                    placeholder="302017"
                    value={formData.stationId}
                    onChange={handleChange("stationId")}
                  />
                </label>

                <label>
                  Station type *
                  <select
                    value={formData.stationType}
                    onChange={handleChange("stationType")}
                  >
                    <option value="Public">Public</option>
                    <option value="Private">Private</option>
                  </select>
                </label>

                <label>
                  POC name *
                  <input
                    type="text"
                    placeholder="Karnataka"
                    value={formData.pocName}
                    onChange={handleChange("pocName")}
                  />
                </label>

                <label>
                  POC number *
                  <input
                    type="text"
                    placeholder="Karnataka"
                    value={formData.pocNumber}
                    onChange={handleChange("pocNumber")}
                  />
                </label>

                <label>
                  POC email ID *
                  <input
                    type="email"
                    placeholder="Karnataka"
                    value={formData.pocEmail}
                    onChange={handleChange("pocEmail")}
                  />
                </label>
              </div>
            </>
          ) : currentStep === 2 ? (
            <>
              <h3>Location details</h3>

              <div className="station-form-grid single-column">
                <label>
                  Street address*
                  <input
                    type="text"
                    placeholder="123 street name, city, extra info"
                    value={formData.streetAddress}
                    onChange={handleChange("streetAddress")}
                  />
                </label>
              </div>

              <div className="station-form-grid two-column">
                <label>
                  State*
                  <input
                    type="text"
                    placeholder="Karnataka"
                    value={formData.state}
                    onChange={handleChange("state")}
                  />
                </label>

                <label>
                  Pincode*
                  <input
                    type="text"
                    placeholder="302017"
                    value={formData.pincode}
                    onChange={handleChange("pincode")}
                  />
                </label>

                <label>
                  Latitude*
                  <input
                    type="text"
                    placeholder="Karnataka"
                    value={formData.latitude}
                    onChange={handleChange("latitude")}
                  />
                </label>

                <label>
                  Longitude*
                  <input
                    type="text"
                    placeholder="302017"
                    value={formData.longitude}
                    onChange={handleChange("longitude")}
                  />
                </label>
              </div>

              <div className="station-form-grid single-column location-full-width">
                <label>
                  Available parking space*
                  <input
                    type="text"
                    placeholder="Eg 20"
                    value={formData.availableParkingSpace}
                    onChange={handleChange("availableParkingSpace")}
                  />
                </label>

                <label>
                  Description*
                  <textarea
                    placeholder="Add address note"
                    value={formData.description}
                    onChange={handleChange("description")}
                  />
                </label>
              </div>

              <div className="location-tips-block">
                <h4>Location tips</h4>
                <p>
                  Provide accurate location details and accessibility
                  information. This helps customers find your station easily and
                  ensures they have the right expectations about facilities
                  available.
                </p>
              </div>
            </>
          ) : currentStep === 3 ? (
            <>
              <h3>Charger configuration</h3>

              <div className="charger-config-card">
                <p className="charger-config-title">Add charger type(1)</p>

                <div className="station-form-grid two-column">
                  <label>
                    Charger type*
                    <select
                      value={formData.chargerType}
                      onChange={handleChange("chargerType")}
                    >
                      <option value="">Select charger type</option>
                      <option value="AC charger">AC charger</option>
                      <option value="DC charger">DC charger</option>
                    </select>
                  </label>

                  <label>
                    Connector type*
                    <select
                      value={formData.connectorType}
                      onChange={handleChange("connectorType")}
                    >
                      <option value="">Select connector type</option>
                      <option value="CCS2">CCS2</option>
                      <option value="Type 2">Type 2</option>
                      <option value="GB/T">GB/T</option>
                    </select>
                  </label>

                  <label>
                    Charger capacity*
                    <input
                      type="text"
                      placeholder="1"
                      value={formData.chargerCapacity}
                      onChange={handleChange("chargerCapacity")}
                    />
                  </label>

                  <label>
                    Connectivity
                    <select
                      value={formData.connectivity}
                      onChange={handleChange("connectivity")}
                    >
                      <option value="">Select connectivity</option>
                      <option value="Ethernet">Ethernet</option>
                      <option value="4G">4G</option>
                      <option value="Wi-Fi">Wi-Fi</option>
                    </select>
                  </label>

                  <label>
                    Station access type
                    <select
                      value={formData.stationAccessType}
                      onChange={handleChange("stationAccessType")}
                    >
                      <option value="">Select access type</option>
                      <option value="Public">Public</option>
                      <option value="Private">Private</option>
                    </select>
                  </label>

                  <label>
                    Charger Access Type
                    <select
                      value={formData.chargerAccessType}
                      onChange={handleChange("chargerAccessType")}
                    >
                      <option value="">Select Access Type</option>
                      <option value="Open">Open</option>
                      <option value="Restricted">Restricted</option>
                    </select>
                  </label>
                </div>

                <button className="add-charger-type-btn" type="button">
                  Add charger type
                </button>
              </div>
            </>
          ) : currentStep === 4 ? (
            <>
              <h3>Pricing configuration</h3>

              <div className="station-form-grid single-column">
                <label>
                  Tariff type*
                  <select
                    value={formData.tariffType}
                    onChange={handleChange("tariffType")}
                  >
                    <option value="">Per kWh..</option>
                    <option value="Per kWh">Per kWh</option>
                    <option value="Per minute">Per minute</option>
                    <option value="Flat fee">Flat fee</option>
                  </select>
                </label>

                <label>
                  Price model*
                  <input
                    type="text"
                    placeholder="Per kWh.."
                    value={formData.priceModel}
                    onChange={handleChange("priceModel")}
                  />
                </label>
              </div>

              <div className="station-form-grid two-column">
                <label>
                  Currency*
                  <input
                    type="text"
                    placeholder="INR"
                    value={formData.currency}
                    onChange={handleChange("currency")}
                  />
                </label>

                <label>
                  Price/kWh*
                  <input
                    type="text"
                    placeholder="₹ 0.00"
                    value={formData.pricePerKwh}
                    onChange={handleChange("pricePerKwh")}
                  />
                </label>
              </div>

              <div className="pricing-toggle-row">
                <div>
                  <h4>24/7 operation</h4>
                  <p>Enable round the clock charging access</p>
                </div>
                <label className="switch-control" aria-label="24/7 operation">
                  <input
                    type="checkbox"
                    checked={formData.isRoundTheClock}
                    onChange={handleToggleRoundTheClock}
                  />
                  <span className="switch-slider" />
                </label>
              </div>
            </>
          ) : currentStep === 5 ? (
            <>
              <h3>Review & Submit</h3>

              <div className="review-sections">
                <div className="review-card">
                  <div className="review-card-header">
                    <h4>Station information</h4>
                    <button
                      className="review-edit-btn"
                      type="button"
                      onClick={() => setCurrentStep(1)}
                    >
                      <Pencil size={14} /> Edit
                    </button>
                  </div>
                  <div className="review-grid">
                    <div>
                      <span>Station name</span>
                      <p>{showValue(formData.stationName)}</p>
                    </div>
                    <div>
                      <span>Station type</span>
                      <p>{showValue(formData.stationType)}</p>
                    </div>
                    <div>
                      <span>Contact email</span>
                      <p>{showValue(formData.pocEmail)}</p>
                    </div>
                    <div>
                      <span>Contact phone</span>
                      <p>{showValue(formData.pocNumber)}</p>
                    </div>
                  </div>
                </div>

                <div className="review-card">
                  <div className="review-card-header">
                    <h4>Location details</h4>
                    <button
                      className="review-edit-btn"
                      type="button"
                      onClick={() => setCurrentStep(2)}
                    >
                      <Pencil size={14} /> Edit
                    </button>
                  </div>
                  <div className="review-grid">
                    <div>
                      <span>Street Address</span>
                      <p>{showValue(formData.streetAddress)}</p>
                    </div>
                    <div>
                      <span>State</span>
                      <p>{showValue(formData.state)}</p>
                    </div>
                    <div>
                      <span>Pincode</span>
                      <p>{showValue(formData.pincode)}</p>
                    </div>
                    <div>
                      <span>Latitude</span>
                      <p>{showValue(formData.latitude)}</p>
                    </div>
                    <div>
                      <span>Longitude</span>
                      <p>{showValue(formData.longitude)}</p>
                    </div>
                    <div>
                      <span>Available parking space</span>
                      <p>{showValue(formData.availableParkingSpace)}</p>
                    </div>
                  </div>
                </div>

                <div className="review-card">
                  <div className="review-card-header">
                    <h4>Charger configuration</h4>
                    <button
                      className="review-edit-btn"
                      type="button"
                      onClick={() => setCurrentStep(3)}
                    >
                      <Pencil size={14} /> Edit
                    </button>
                  </div>
                  <div className="review-grid">
                    <div>
                      <span>Charger type</span>
                      <p>{showValue(formData.chargerType)}</p>
                    </div>
                    <div>
                      <span>Connector type</span>
                      <p>{showValue(formData.connectorType)}</p>
                    </div>
                    <div>
                      <span>Power output</span>
                      <p>{showValue(formData.chargerCapacity)}</p>
                    </div>
                    <div>
                      <span>Station access type</span>
                      <p>{showValue(formData.stationAccessType)}</p>
                    </div>
                  </div>
                </div>

                <div className="review-card">
                  <div className="review-card-header">
                    <h4>Pricing configuration</h4>
                    <button
                      className="review-edit-btn"
                      type="button"
                      onClick={() => setCurrentStep(4)}
                    >
                      <Pencil size={14} /> Edit
                    </button>
                  </div>
                  <div className="review-grid">
                    <div>
                      <span>Pricing model</span>
                      <p>{showValue(formData.priceModel)}</p>
                    </div>
                    <div>
                      <span>Currency</span>
                      <p>{showValue(formData.currency)}</p>
                    </div>
                    <div>
                      <span>Price/kWh</span>
                      <p>{showValue(formData.pricePerKwh)}</p>
                    </div>
                    <div>
                      <span>24/7 operation</span>
                      <p>{showValue(formData.isRoundTheClock)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="step-content-placeholder">
              <h3>{steps[currentStep - 1]}</h3>
              <p>Configure this section and click next to continue.</p>
            </div>
          )}

          {currentStep === steps.length ? (
            <div className="station-form-actions review-actions">
              <button className="primary-btn create-station-btn" type="button">
                Create station
              </button>
            </div>
          ) : (
            <div className="station-form-actions">
              <button
                className="secondary-btn"
                onClick={goToPreviousStep}
                disabled={currentStep === 1}
              >
                <ArrowLeft size={16} />
                Previous
              </button>
              <button className="primary-btn" onClick={goToNextStep}>
                Next
                <ArrowRight size={16} />
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AddStationFlow;
